import { fmtDate } from '../../utils/datetime';
import { toast } from '../../utils/toast';
import React, { useState, useMemo, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Edit, Trash2, Search, Filter, X } from 'lucide-react';
import { DoctorListProps, Doctor } from '../common/types';
import { DoctorCategory, DoctorSpecialization } from '../master/utils/interface';
import { getAllDoctorCategories, getAllDoctorSpecialization, PaginationMeta, DoctorServerFilters } from '../../api/doctor';
import { getAllHeadquarters, getAllLocations } from '../../api/master';
import { useAuth } from '../../context/AuthContext';

interface HeadQuarter { id: number; name: string; }
interface Location    { id: number; name: string; head_quarter_id: number; }

interface DoctorListExtendedProps extends DoctorListProps {
  pagination?: PaginationMeta;
  onPageChange?: (page: number) => void;
  serverFilters?: DoctorServerFilters;
  onServerFilterChange?: (filters: DoctorServerFilters) => void;
}

const DoctorList: React.FC<DoctorListExtendedProps> = ({
  doctors, isLoading, onEdit, onDelete,
  pagination, onPageChange,
  serverFilters = {}, onServerFilterChange,
}) => {
  const [searchTerm, setSearchTerm] = useState(serverFilters.search ?? '');
  const [clientFilters, setClientFilters] = useState({ specialization_id: 0, doctor_category_id: 0 });
  const [showFilters, setShowFilters] = useState(false);
  const [headquarters, setHeadquarters] = useState<HeadQuarter[]>([]);
  const [allLocations, setAllLocations] = useState<Location[]>([]);
  const [specializations, setSpecializations] = useState<DoctorSpecialization[]>([]);
  const [categories, setCategories] = useState<DoctorCategory[]>([]);
  const { user } = useAuth();

  useEffect(() => {
    if (!user?.tenant_id) return;
    const fetchDropdowns = async () => {
      try {
        const [specs, cats, hqs, locs] = await Promise.all([
          getAllDoctorSpecialization(user.tenant_id),
          getAllDoctorCategories(user.tenant_id),
          getAllHeadquarters(user.tenant_id),
          getAllLocations(user.tenant_id),
        ]);
        setSpecializations(specs);
        setCategories(cats);
        setHeadquarters(hqs);
        setAllLocations(locs);
      } catch (error) {
        console.error('Error fetching filter data:', error);
      }
    };
    fetchDropdowns();
  }, [user?.tenant_id]);

  // Debounce search input and push it to the server as a filter so it
  // searches the whole dataset, not just the currently loaded page.
  useEffect(() => {
    const handle = setTimeout(() => {
      if ((serverFilters.search ?? '') !== searchTerm) {
        onServerFilterChange?.({ ...serverFilters, search: searchTerm || undefined });
      }
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Locations filtered to the selected HQ
  const filteredLocations = serverFilters.head_quarter_id
    ? allLocations.filter(l => l.head_quarter_id === serverFilters.head_quarter_id)
    : allLocations;

  const handleHqChange = (value: string) => {
    const hqId = value ? Number(value) : undefined;
    // Reset location when HQ changes
    onServerFilterChange?.({ ...serverFilters, head_quarter_id: hqId, location_id: undefined });
  };

  const handleLocationChange = (value: string) => {
    const locationId = value ? Number(value) : undefined;
    onServerFilterChange?.({ ...serverFilters, location_id: locationId });
  };

  // Search is applied server-side (see debounce effect above) so that it
  // matches across the entire dataset rather than only the loaded page.
  const filteredDoctors = useMemo(() => {
    return doctors.filter(doctor => {
      const specialtyMatch = !clientFilters.specialization_id || doctor.specialization_id === clientFilters.specialization_id;
      const categoryMatch = !clientFilters.doctor_category_id || doctor.doctor_category_id === clientFilters.doctor_category_id;
      return specialtyMatch && categoryMatch;
    });
  }, [doctors, clientFilters]);

  const hasServerFilters = !!(serverFilters.head_quarter_id || serverFilters.location_id);
  const hasClientFilters = !!(searchTerm || clientFilters.specialization_id || clientFilters.doctor_category_id);
  const hasActiveFilters = hasServerFilters || hasClientFilters;

  const clearAllFilters = () => {
    setSearchTerm('');
    setClientFilters({ specialization_id: 0, doctor_category_id: 0 });
    onServerFilterChange?.({});
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-2">Loading doctors...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Search and Filter Header */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex flex-col md:flex-row gap-3">
          {/* Search Bar */}
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search by name, code, hospital, qualification..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* HQ Filter — server-side */}
          <select
            value={serverFilters.head_quarter_id ?? ''}
            onChange={(e) => handleHqChange(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="">All Headquarters</option>
            {headquarters.map(hq => (
              <option key={hq.id} value={hq.id}>{hq.name}</option>
            ))}
          </select>

          {/* Location Filter — server-side, scoped to selected HQ */}
          <select
            value={serverFilters.location_id ?? ''}
            onChange={(e) => handleLocationChange(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="">{serverFilters.head_quarter_id ? 'All Locations' : 'All Locations'}</option>
            {filteredLocations.map(loc => (
              <option key={loc.id} value={loc.id}>{loc.name}</option>
            ))}
          </select>

          {/* More Filters Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center px-4 py-2 border rounded-lg transition-colors whitespace-nowrap ${
              showFilters || hasClientFilters
                ? 'bg-blue-50 border-blue-300 text-blue-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-4 h-4 mr-2" />
            More
            {hasClientFilters && (
              <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full">●</span>
            )}
          </button>

          {hasActiveFilters && (
            <button onClick={clearAllFilters} className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-800 transition-colors">
              <X className="w-4 h-4 mr-1" />
              Clear
            </button>
          )}
        </div>

        {/* Expandable: Specialty + Category filters (client-side on current page) */}
        {showFilters && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Specialty</label>
              <select
                value={clientFilters.specialization_id}
                onChange={(e) => setClientFilters(prev => ({ ...prev, specialization_id: parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="0">All Specialties</option>
                {specializations.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
              <select
                value={clientFilters.doctor_category_id}
                onChange={(e) => setClientFilters(prev => ({ ...prev, doctor_category_id: parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="0">All Categories</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Results Summary */}
        <div className="mt-3 text-sm text-gray-600">
          {pagination ? (
            <>
              Showing {((pagination.current_page - 1) * pagination.per_page) + 1}–{Math.min(pagination.current_page * pagination.per_page, pagination.total)} of{' '}
              <span className="font-medium">{pagination.total}</span> doctors
              {hasActiveFilters && <span className="ml-1 text-blue-600">(filtered)</span>}
            </>
          ) : (
            <>Showing {filteredDoctors.length} doctors{hasActiveFilters && ' (filtered)'}</>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Doctor Code
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Doctor Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                HQ
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Hospital Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Employee
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Specialty
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Qualification
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Zone
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date of Birth
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Address
              </th>
              {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created Date
              </th> */}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredDoctors.length === 0 ? (
              <tr>
                <td colSpan={14} className="px-6 py-8 text-center text-gray-500">
                  {hasActiveFilters ? 'No doctors match the current filters.' : 'No doctors found.'}
                </td>
              </tr>
            ) : (
              filteredDoctors.map((doctor) => (
                <tr key={doctor.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => onEdit(doctor)}
                        className="text-blue-600 hover:text-blue-900 transition-colors"
                        title="Edit doctor"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={async () => {
                          if (await toast.confirm('This action cannot be undone.', 'Delete doctor?', 'Delete')) {
                            onDelete(doctor.id!.toString());
                          }
                        }}
                        className="text-red-600 hover:text-red-900 transition-colors"
                        title="Delete doctor"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                    {doctor.code || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {doctor.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {doctor.head_quarter || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {doctor.city || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {doctor.hospital_name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {doctor.employee}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {doctor.specialization}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {doctor.qualification || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {doctor.doctor_category ? (
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full`}>
                        {doctor.doctor_category}
                      </span>
                    ) : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {doctor.zone}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {doctor.dob ? fmtDate(doctor.dob) : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate">
                    {doctor.address || '-'}
                  </td>
                  {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {doctor.created_date ? fmtDate(doctor.created_date) : '-'}
                  </td> */}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer: count + pagination */}
      <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <p className="text-sm text-gray-600">
          {pagination
            ? <>Showing {((pagination.current_page - 1) * pagination.per_page) + 1}–{Math.min(pagination.current_page * pagination.per_page, pagination.total)} of <span className="font-medium">{pagination.total}</span></>
            : <>Total: <span className="font-medium">{doctors.length}</span></>}
          {hasActiveFilters && <span className="ml-2 text-blue-600">(filtered)</span>}
        </p>

        {pagination && pagination.last_page > 1 && onPageChange && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => onPageChange(pagination.current_page - 1)}
              disabled={pagination.current_page === 1}
              className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {Array.from({ length: pagination.last_page }, (_, i) => i + 1)
              .filter(p => p === 1 || p === pagination.last_page || Math.abs(p - pagination.current_page) <= 1)
              .map((p, idx, arr) => (
                <React.Fragment key={p}>
                  {idx > 0 && arr[idx - 1] !== p - 1 && (
                    <span className="px-1 text-gray-400 text-sm">…</span>
                  )}
                  <button
                    onClick={() => onPageChange(p)}
                    className={`min-w-[32px] px-2 py-1.5 text-sm rounded-lg ${
                      p === pagination.current_page
                        ? 'bg-blue-600 text-white font-medium'
                        : 'border border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    {p}
                  </button>
                </React.Fragment>
              ))}

            <button
              onClick={() => onPageChange(pagination.current_page + 1)}
              disabled={pagination.current_page === pagination.last_page}
              className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DoctorList;